import { useEffect, useRef, useState, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Download, Lock, FileArchive, Terminal, Copy, Check } from 'lucide-react'
import { copyToClipboard } from '@/utils/clipboard'

interface ShareGroupInfo {
  shareName: string
  fileCount: number
  totalSize: number
  downloadCount: number
  maxDownloads: number
  expireAt: string
  isExpired: boolean
  passwordProtected: boolean
  burnAfterRead: boolean
  description?: string  // 备注信息
}

export default function ShareGroupDownload() {
  const { shareKey } = useParams<{ shareKey: string }>()
  const [info, setInfo] = useState<ShareGroupInfo | null>(null)
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [activeCliTab, setActiveCliTab] = useState<'wget' | 'curl'>('wget')
  const [copiedCli, setCopiedCli] = useState(false)
  const downloadFiredRef = useRef(false)

  useEffect(() => {
    console.log('[ShareGroup] useEffect - shareKey:', shareKey)
    console.log('[ShareGroup] useEffect - window.location:', window.location.href)
    
    if (shareKey) {
      loadShareGroupInfo()
    } else {
      console.error('[ShareGroup] shareKey 为空!')
    }
  }, [shareKey])

  const loadShareGroupInfo = async () => {
    try {
      console.log('[ShareGroup] 加载分享组信息:', shareKey)
      const url = `/api/download/group/${shareKey}/info`
      console.log('[ShareGroup] 请求URL:', url)
      
      const response = await fetch(url)
      console.log('[ShareGroup] 响应状态:', response.status)
      
      if (response.ok) {
        const data = await response.json()
        console.log('[ShareGroup] 响应数据:', data)
        setInfo(data.data)
      } else {
        const errorData = await response.json().catch(() => ({}))
        console.error('[ShareGroup] 错误响应:', errorData)
        
        // 根据错误状态码和reason显示不同的错误信息
        let errorMessage = '加载失败'
        if (response.status === 404) {
          errorMessage = errorData.message || '分享不存在，请检查链接是否正确'
        } else if (response.status === 410) {
          // 410 Gone - 资源已不可用
          switch (errorData.reason) {
            case 'burned':
              errorMessage = '😔 此分享已被销毁\n\n下载后自动销毁的文件只能下载一次'
              break
            case 'expired':
              errorMessage = '⏰ 此分享已过期\n\n分享链接已超过有效期'
              break
            case 'deleted':
              errorMessage = '🗑️ 此分享已被删除\n\n文件所有者已删除此分享'
              break
            default:
              errorMessage = errorData.message || '此分享已不可用'
          }
        } else {
          errorMessage = errorData.message || '加载失败，请稍后重试'
        }
        
        setError(errorMessage)
      }
    } catch (err) {
      console.error('[ShareGroup] 请求失败:', err)
      setError('网络错误，请检查网络连接')
    }
  }

  const handleDownload = async () => {
    if (!shareKey || downloadFiredRef.current) return

    downloadFiredRef.current = true
    setLoading(true)
    setError('')

    try {
      const verifyFormData = new FormData()
      if (password) {
        verifyFormData.append('password', password)
      }

      // 1. 先进行轻量级预验证（不消耗下载次数，不触发阅后即焚）
      const verifyUrl = `/api/download/group/${shareKey}/verify`
      console.log('[ShareGroup] 验证URL:', verifyUrl)

      const verifyRes = await fetch(verifyUrl, {
        method: 'POST',
        body: verifyFormData,
      })

      console.log('[ShareGroup] 预验证响应状态:', verifyRes.status)

      if (!verifyRes.ok) {
        const errorData = await verifyRes.json().catch(() => ({}))
        console.error('[ShareGroup] 验证错误:', errorData)

        let errorMessage = '下载失败'
        if (verifyRes.status === 404) {
          errorMessage = errorData.message || '分享不存在'
        } else if (verifyRes.status === 410) {
          switch (errorData.reason) {
            case 'burned':
              errorMessage = '😔 此分享已被销毁\n\n下载后自动销毁的文件只能下载一次'
              break
            case 'expired':
              errorMessage = '⏰ 此分享已过期\n\n分享链接已超过有效期'
              break
            case 'deleted':
              errorMessage = '🗑️ 此分享已被删除\n\n文件所有者已删除此分享'
              break
            default:
              errorMessage = errorData.message || '此分享已不可用'
          }
        } else if (verifyRes.status === 401) {
          errorMessage = '❌ 访问密码错误\n\n请输入正确的访问密码'
        } else if (verifyRes.status === 429) {
          errorMessage = '⚠️ 下载次数已达上限\n\n此分享已达到最大下载次数'
        } else if (verifyRes.status === 500) {
          errorMessage = errorData.message || '服务器错误，请稍后重试'
        } else {
          errorMessage = errorData.message || '下载失败，请稍后重试'
        }

        setError(errorMessage)
        downloadFiredRef.current = false // 失败可重试
        return
      }

      // 2. 预验证通过后，优先使用下载令牌触发顶层导航下载
      const verifyData = await verifyRes.json().catch(() => ({}))
      const downloadToken: string | undefined = verifyData?.data?.downloadToken

      if (downloadToken) {
        const downloadUrl = `/api/download/group/${shareKey}?token=${encodeURIComponent(downloadToken)}`
        console.log('[ShareGroup] 触发浏览器下载（顶层导航+令牌）:', downloadUrl)
        window.location.href = downloadUrl
      } else {
        // 回退隐藏表单 POST
        const downloadUrl = `/api/download/group/${shareKey}`
        console.log('[ShareGroup] 触发原生流式下载:', downloadUrl)

        const form = document.createElement('form')
        form.method = 'POST'
        form.action = downloadUrl
        form.style.display = 'none'

        if (password) {
          const pwdInput = document.createElement('input')
          pwdInput.type = 'hidden'
          pwdInput.name = 'password'
          pwdInput.value = password
          form.appendChild(pwdInput)
        }

        document.body.appendChild(form)
        form.submit()
        document.body.removeChild(form)
      }

      // 更新界面下载计数
      if (info) {
        setInfo(prev => prev ? { ...prev, downloadCount: prev.downloadCount + 1 } : null)
      }

      // 若为阅后即焚，提醒用户
      if (info?.burnAfterRead) {
        setTimeout(() => {
          setError('😔 此分享已被销毁\n\n下载后自动销毁的文件只能下载一次')
        }, 3000)
      }
    } catch (err) {
      console.error('[ShareGroup] 下载请求失败:', err)
      setError('网络错误，请检查网络连接')
      downloadFiredRef.current = false
    } finally {
      setLoading(false)
    }
  }

  const cliCommand = useMemo(() => {
    if (!shareKey) return ''
    const base = `${window.location.origin}/api/download/group/${shareKey}`
    let url = base
    if (info?.passwordProtected) {
      const pwd = password.trim()
      const pwdParam = pwd ? encodeURIComponent(pwd) : '<访问口令>'
      url += `?password=${pwdParam}`
    }
    if (activeCliTab === 'wget') {
      return `wget --content-disposition "${url}"`
    }
    return `curl -OJ "${url}"`
  }, [shareKey, info?.passwordProtected, password, activeCliTab])

  const handleCopyCli = async () => {
    if (!cliCommand) return
    const success = await copyToClipboard(cliCommand)
    if (success) {
      setCopiedCli(true)
      setTimeout(() => setCopiedCli(false), 2000)
    }
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
  }

  if (error && !info) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <FileArchive className="w-16 h-16 mx-auto text-gray-400 mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">分享不存在</h2>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    )
  }

  if (!info) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-600">加载中...</div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
        <div className="text-center mb-6">
          <FileArchive className="w-16 h-16 mx-auto text-blue-500 mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            {info.shareName}
          </h1>
          <div className="flex items-center justify-center gap-4 text-sm text-gray-600 mb-3">
            <span>{info.fileCount} 个文件</span>
            <span>·</span>
            <span>{formatFileSize(info.totalSize)}</span>
          </div>
          {info.description && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-left">
              <p className="text-sm font-medium text-gray-700 mb-1">备注信息</p>
              <p className="text-sm text-gray-600 whitespace-pre-wrap">{info.description}</p>
            </div>
          )}
        </div>

        {info.isExpired && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
            <p className="text-red-600 text-sm">此分享已过期</p>
          </div>
        )}

        {info.burnAfterRead && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
            <p className="text-yellow-600 text-sm">
              ⚠️ 此分享下载后将自动销毁
            </p>
          </div>
        )}

        {info.maxDownloads > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <p className="text-blue-600 text-sm">
              已下载 {info.downloadCount} / {info.maxDownloads} 次
            </p>
          </div>
        )}

        {info.passwordProtected && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Lock className="inline w-4 h-4 mr-1 text-gray-500" />
              访问口令
            </label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="请输入访问口令"
              className="w-full"
              autoFocus
            />
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
            <p className="text-red-600 text-sm whitespace-pre-wrap">{error}</p>
          </div>
        )}

        <Button
          onClick={() => void handleDownload()}
          disabled={loading || info.isExpired}
          className="w-full bg-yellow-400 hover:bg-yellow-500 text-gray-900 font-semibold shadow-sm rounded-full h-11"
        >
          <Download className="w-4 h-4 mr-2" />
          {loading ? '下载处理中...' : '立即下载文件'}
        </Button>

        {/* 终端命令行快捷下载 */}
        <div className="mt-6 pt-5 border-t border-gray-100">
          <div className="flex items-center justify-between mb-2.5">
            <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-700">
              <Terminal className="w-3.5 h-3.5 text-gray-500" />
              <span>终端命令行下载</span>
            </div>
            <div className="flex items-center bg-gray-100 p-0.5 rounded-lg text-xs font-medium">
              <button
                type="button"
                onClick={() => setActiveCliTab('wget')}
                className={`px-2.5 py-1 rounded-md transition-all ${
                  activeCliTab === 'wget'
                    ? 'bg-white text-gray-900 shadow-sm font-semibold'
                    : 'text-gray-500 hover:text-gray-900'
                }`}
              >
                wget
              </button>
              <button
                type="button"
                onClick={() => setActiveCliTab('curl')}
                className={`px-2.5 py-1 rounded-md transition-all ${
                  activeCliTab === 'curl'
                    ? 'bg-white text-gray-900 shadow-sm font-semibold'
                    : 'text-gray-500 hover:text-gray-900'
                }`}
              >
                curl
              </button>
            </div>
          </div>

          <div className="relative group bg-gray-900 rounded-lg p-3 text-left">
            <pre className="font-mono text-xs text-gray-200 overflow-x-auto whitespace-pre-wrap break-all pr-8 select-all">
              {cliCommand}
            </pre>
            <button
              type="button"
              onClick={handleCopyCli}
              title="复制命令"
              className="absolute top-2.5 right-2.5 p-1.5 rounded-md bg-gray-800 hover:bg-gray-700 text-gray-300 hover:text-white transition-colors"
            >
              {copiedCli ? (
                <Check className="w-3.5 h-3.5 text-green-400" />
              ) : (
                <Copy className="w-3.5 h-3.5" />
              )}
            </button>
          </div>

          <div className="mt-2 flex items-center justify-between text-[11px] text-gray-400 px-0.5">
            <span>
              {info.passwordProtected && !password.trim() ? (
                <span className="text-amber-500">提示：输入口令后命令将自动更新</span>
              ) : (
                <span>点击右侧按钮复制命令，在终端粘贴运行</span>
              )}
            </span>
            {copiedCli && (
              <span className="text-green-600 font-medium">已复制到剪贴板</span>
            )}
          </div>
        </div>

        <div className="mt-4 text-center text-xs text-gray-500">
          过期时间：{new Date(info.expireAt).toLocaleString()}
        </div>
      </div>
    </div>
  )
}
